const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/kisapi/reports.ts';
import Imm from 'immutable';
import { ReportTypes } from '../../defs/reportDefs';
import { compileEndpoint } from './utils';
import { KeyValuePair } from '../../defs/generalDefs';

const REPORT_FIELDS_EXCLUDING_CONTENT = [
    'CCODE',
    'DAT',
    'FALLNR',
    'FCODE',
    'LAST_MODIFIED',
    'MUTDAT',
    'MUTVIS',
    'NAME',
    'ORDERNR',
    'PATNR',
    'RECEIVER',
    'REPORTNR',
    'SEENDAT',
    'SEENVIS',
    'SENDER',
    'STATUS',
    'TERMNR',
    'VERSION',
    'VISUM',
].join(',');

/**
 * Loads the object-specific rights of a given report.
 */
export function apiLoadReportRights(sender: string, reportnr: string) {
    return {
        endpoint: compileEndpoint(
            ['Reports', sender || '?', reportnr || '?', 'Rights'], //
            null
        ),
    };
}

/**
 * Loads the list of single-result lab reports belonging to a given patient.
 */
export function apiLoadLabSingleReports(patnr: string, mindat: string, maxdat: string, fcode?: string) {
    return {
        endpoint: compileEndpoint(
            ['LarsReports'],
            [
                { key: 'PATNR', value: patnr }, //
                { key: 'MINDAT', value: mindat },
                { key: 'MAXDAT', value: maxdat },
                fcode && { key: 'FCODE', value: fcode },
            ]
        ),
        requestConfig: {
            headers: { Accept: 'array=no' },
        },
    };
}

/**
 * Loads the record of a particular single-result lab report
 */
export function apiLoad1LabSingleReport(sender: string, reportnr: string) {
    return {
        endpoint: compileEndpoint(
            ['LarsReports', sender || '?', reportnr || '?'], //
            null
        ),
    };
}

/**
 * Updates the record of a single-result lab report. This is mostly used for setting the STATUS.
 * @param sender
 * @param reportnr
 * @param etag
 * @param updateRec should contain the members to be updated only
 */
export function apiUpdateLabSingleReport(
    sender: string,
    reportnr: string,
    etag: string,
    updateRec: Imm.Map<string, any>
) {
    return {
        endpoint: compileEndpoint(
            ['LarsReports', sender || '?', reportnr || '?'], //
            null
        ),
        requestConfig: {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'if-match': etag,
            },
            body: JSON.stringify(updateRec.toJS()),
        },
    };
}

/**
 * Loads the object-specific rights of a given single-result lab report.
 */
export function apiLoadLabSingleReportRights(sender: string, reportnr: string) {
    return {
        endpoint: compileEndpoint(
            ['LarsReports', sender || '?', reportnr || '?', 'Rights'], //
            null
        ),
    };
}

/**
 * Retrieves a report as a pdf document
 */
export function apiLoadReportAsPDF(sender: string, reportNr: string, reportType: ReportTypes = 'regular') {
    return {
        endpoint: compileEndpoint(
            [reportType === 'lars' ? 'LarsReports' : 'Reports', sender || '?', reportNr || '?'],
            null
        ),
        requestConfig: {
            headers: { accept: 'application/pdf' },
        },
    };
}

/**
 * Retrieves the actual record of a specific report
 */
export function apiLoad1Report(
    sender: string,
    reportNr: string,
    reportType: ReportTypes = 'regular',
    disallowArrays: boolean = false,
    withContent: boolean = true
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    const fields = withContent ? `${REPORT_FIELDS_EXCLUDING_CONTENT},CONTENT` : REPORT_FIELDS_EXCLUDING_CONTENT;

    return {
        endpoint: compileEndpoint(
            [reportType === 'lars' ? 'LarsReports' : 'Reports', sender || '?', reportNr || '?'],
            [{ key: 'Fields', value: fields }]
        ),
        requestConfig,
    };
}

/**
 * Updates the record of a specific report
 * @param sender
 * @param reportNr
 * @param etag
 * @param updateRec should contain the members to be updated only
 * @param reportType
 */
export function apiUpdateReport(
    sender: string,
    reportNr: string,
    etag: string,
    updateRec: Imm.Map<string, any>,
    reportType: ReportTypes = 'regular'
) {
    return {
        endpoint: compileEndpoint(
            [reportType === 'lars' ? 'LarsReports' : 'Reports', sender || '?', reportNr || '?'],
            null
        ),
        requestConfig: {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'if-match': etag,
            },
            body: JSON.stringify(updateRec.toJS()),
        },
    };
}

export function apiCreateReport(patnr: string, fallnr: string, fcode: string, name: string, content: KeyValuePair) {
    return {
        endpoint: compileEndpoint(['Reports'], null),
        requestConfig: {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                FCODE: fcode,
                PATNR: patnr,
                FALLNR: fallnr,
                NAME: name,
                CONTENT: content,
            }),
        },
    };
}

/**
 * Fetches a list of reports for a given patient.
 * @param patnr the patient's id
 * @param reportType
 * @param fallnr
 * @param mindat YYYY-MM-DD HH:mm:ss
 * @param maxdat YYYY-MM-DD HH:mm:ss
 * @param requiredFcodes
 * @param excludedFcodes
 * @param fcodeLike
 * @param kind
 * @param typ
 * @param status
 * @param transformRtf
 * @param withContent specifies how the CONTENT should be loaded:
 *    true=load the full content
 *    false|undefined=don't load the CONTENT
 *    string[]=return only the given Qscript-paths of the CONTENT (path elements delimited by slash)
 * @param useMutDat
 * @param onlyMostRecent
 * @param reportsFilter Icode for a reportsfilter, this will be applied to the result directly in endpoint
 * @param sortOrder
 */
export function apiLoadReports(
    patnr: string,
    reportType: ReportTypes = 'regular',
    fallnr?: string,
    mindat?: string,
    maxdat?: string,
    requiredFcodes?: string[],
    excludedFcodes?: string[],
    fcodeLike?: string,
    kind?: string,
    typ?: string,
    status?: string,
    transformRtf?: boolean,
    withContent?: boolean | string[],
    useMutDat?: boolean,
    onlyMostRecent?: boolean,
    reportsFilter?: string,
    sortOrder: 'ASC' | 'DESC' = 'ASC'
) {
    const contentPaths = Array.isArray(withContent) && withContent.length > 0 && withContent.join(',');
    return {
        endpoint: compileEndpoint(
            [reportType === 'lars' ? 'LarsReports' : 'Reports'],
            [
                { key: 'PATNR', value: patnr },
                fallnr && { key: 'FallFilter', value: fallnr },
                mindat && { key: 'MINDAT', value: mindat },
                maxdat && { key: 'MAXDAT', value: maxdat },
                requiredFcodes && { key: 'INFCODE', value: requiredFcodes.join(',') },
                excludedFcodes && { key: 'EXCLUDEFCODE', value: excludedFcodes.join(',') },
                fcodeLike && { key: 'FCODE', value: fcodeLike },
                kind && { key: 'KIND', value: kind },
                typ && { key: 'TYP', value: typ },
                status && { key: 'STATUS', value: status },
                useMutDat && { key: 'USEMUTDAT', value: '1' },
                transformRtf && { key: 'Trf', value: 'RtfToAnsi' },
                { key: 'Fields', value: withContent === true ? 'ALLCONTENT' : 'ALL' },
                contentPaths && { key: 'ContentPaths', value: contentPaths },
                onlyMostRecent && { key: 'OnlyMostRecent', value: '1' },
                reportsFilter && { key: 'ReportsFilter', value: reportsFilter },
                { key: 'SORTORDER', value: sortOrder },
            ]
        ),
        requestConfig: {
            headers: { Accept: 'array=no' },
        },
    };
}
